import { observer } from 'mobx-react-lite';
import React, { SyntheticEvent, useState } from 'react';
import { Card, Header, Tab, Image, Grid, Button } from 'semantic-ui-react';
import { Photo, Profile } from '../../app/models/profile';
import { useStore } from '../../app/stores/store';
import { PhotoUploadWidget } from '../../app/common/imageUpload/PhotoUploadWidget';

interface Props {
  profile: Profile;
}

const ProfilePhotos: React.FC<Props> = observer(({ profile }) => {
  const {
    profileStore: {
      isCurrentUser,
      uploadPhoto,
      uploading,
      setMainPhoto,
      deletePhoto,
      loading,
    },
  } = useStore();
  const [addPhotoMode, setAddPhotoMode] = useState(false);
  const [buttonTarget, setButtonTarget] = useState('');

  const handlePhotoUpload = async (file: Blob) => {
    await uploadPhoto(file);
    setAddPhotoMode(false);
  };

  const handleSetMainPhoto = async (
    photo: Photo,
    event: SyntheticEvent<HTMLButtonElement>,
  ) => {
    setButtonTarget(event.currentTarget.name);
    await setMainPhoto(photo);
    setButtonTarget('');
  };

  const handleDeletePhoto = async (
    photo: Photo,
    event: SyntheticEvent<HTMLButtonElement>,
  ) => {
    setButtonTarget(event.currentTarget.name);
    await deletePhoto(photo);
    setButtonTarget('');
  };

  return (
    <Tab.Pane>
      <Grid>
        <Grid.Column width={16}>
          <Header floated="left" icon="image" content="Photos" />
          {isCurrentUser && (
            <Button
              floated="right"
              basic
              onClick={() => setAddPhotoMode(!addPhotoMode)}
              content={addPhotoMode ? 'Cancel' : 'Add Photo'}
            />
          )}
        </Grid.Column>
        <Grid.Column width={16}>
          {addPhotoMode ? (
            <PhotoUploadWidget
              uploadPhoto={handlePhotoUpload}
              loading={uploading}
            />
          ) : (
            <Card.Group itemsPerRow={5}>
              {profile.photos?.map((photo) => (
                <Card key={photo.id}>
                  <Image src={photo.url} />
                  {isCurrentUser && (
                    <Button.Group fluid widths={2}>
                      <Button
                        basic
                        color="green"
                        content="Main"
                        name={'main' + photo.id}
                        disabled={photo.isMain}
                        loading={loading && buttonTarget === 'main' + photo.id}
                        onClick={(event) => handleSetMainPhoto(photo, event)}
                      />
                      <Button
                        basic
                        color="red"
                        icon="trash"
                        name={photo.id}
                        disabled={photo.isMain}
                        loading={loading && buttonTarget === photo.id}
                        onClick={(event) => handleDeletePhoto(photo, event)}
                      />
                    </Button.Group>
                  )}
                </Card>
              ))}
            </Card.Group>
          )}
        </Grid.Column>
      </Grid>
    </Tab.Pane>
  );
});

export { ProfilePhotos };
